import React, { useState } from 'react';
import { graphql, Link } from 'gatsby';
import { Sticky, StickyContainer } from 'react-sticky';
import classnames from 'classnames';
import Img from 'graphcms-image';

import FAQCard from '../components/FAQCard';
import { DefaultLogoReel } from '../components/LogoReel';
import PricingFeatureTable from '../components/PricingFeatureTable';
import PricingPlanCard from '../components/PricingPlanCard';
import Toggle from '../components/Toggle';
import Pill from '../components/Pill';
import USPCardRedesigned from '../components/USPCardRedesigned';

const PricingBaseTemplate = ({
  data: {
    cards,
    complianceFeatures,
    contentFeatures,
    developmentFeatures,
    extensibilityFeatures,
    infrastructureFeatures,
    servicesFeatures,
    pricingFaqCategory,
    plans,
    limitFeatures,
  },
  pageContext: { page },
}) => {
  const [annualBilledActive, setAnnualBilledActive] = useState(true);

  const [contentBlock] = page.contentBlocks;

  return (
    <div className="py-20 space-y-20">
      <section className="max-w-6xl mx-auto px-4 md:px-6 space-y-12">
        <div className="mx-auto space-y-6 text-center w-full md:w-3/4">
          <h1 className="font-bold leading-tight text-3xl md:text-5xl">
            {page.title}
          </h1>
          <p className="leading-relaxed text-lg">{page.subTitle}</p>
        </div>
        <Toggle
          labels={[
            'Monthly',
            () => (
              <div>
                Yearly <Pill colour="purple" label="Save up to 25%" />
              </div>
            ),
          ]}
          toggle={setAnnualBilledActive}
          toggleValue={annualBilledActive}
        />
        <div className="grid lg:grid-cols-3 gap-6">
          {cards.nodes.map((card) => (
            <PricingPlanCard
              key={card.id}
              annualBilledActive={annualBilledActive}
              {...card}
            />
          ))}
        </div>
      </section>
      <DefaultLogoReel />
      <StickyContainer className="relative">
        <Sticky bottomOffset={200} topOffset={200}>
          {({ isSticky, style }) => (
            <div style={style}>
              <div
                className={classnames('bg-whisper border-b w-full', {
                  hidden: !isSticky,
                  'absolute md:block py-8': isSticky,
                })}
                style={isSticky ? { top: '88px' } : null}
              >
                <div className="max-w-6xl mx-auto px-4 md:px-6">
                  <div className="grid grid-cols-4 md:grid-cols-6">
                    <div className="hidden md:block col-span-2">
                      <h4 className="font-medium lg:text-xl">All features</h4>
                    </div>
                    {plans.nodes.map((plan) => (
                      <div key={plan.id} className="space-y-5">
                        <h4 className="font-bold text-sm lg:text-xl">
                          {plan.name}
                        </h4>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Sticky>
        <section className="max-w-6xl mx-auto px-4 md:px-6 space-y-12">
          <div className="mx-auto space-y-6 text-center w-full md:w-3/4">
            <h2 className="font-bold leading-tight text-3xl md:text-5xl">
              Compare Plans
            </h2>
          </div>
          <div>
            <div className="grid grid-cols-4 md:grid-cols-6">
              <div className="hidden md:block col-span-2 space-y-5">
                <h4 className="font-medium lg:text-2xl">All features</h4>
                <div className="border-t py-5">
                  <p className="font-medium text-cornflower text-xs">
                    Save up to 25% on yearly subscription
                  </p>
                </div>
              </div>
              {plans.nodes.map((plan) => {
                const planPrice = (plan) => {
                  switch (plan.name) {
                    case 'Community':
                      return 'Free forever';
                    case 'Enterprise':
                      return 'Custom';
                    default:
                      return annualBilledActive
                        ? plan.formattedAnnualPrice
                        : plan.formattedMonthlyPrice;
                  }
                };

                return (
                  <div key={plan.id} className="space-y-5">
                    <h4 className="font-bold text-sm lg:text-2xl">
                      {plan.name}
                    </h4>
                    <div className="border-t py-5">
                      <p className="font-bold text-sm lg:text-xl">
                        {planPrice(plan)}
                      </p>

                      {(plan.annualPrice || plan.monthlyPrice) && (
                        <p className="text-light-haiti text-xs">
                          {annualBilledActive
                            ? 'per project per month (billed yearly)'
                            : 'per project per month (billed monthly)'}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <PricingFeatureTable
              title="Project Limits"
              features={limitFeatures.nodes}
              plans={plans.nodes}
            />
            <PricingFeatureTable
              title="Development &amp; Deployment"
              features={developmentFeatures.nodes}
              plans={plans.nodes}
            />
            <PricingFeatureTable
              title="Content Editing"
              features={contentFeatures.nodes}
              plans={plans.nodes}
            />
            <PricingFeatureTable
              title="Extensibility"
              features={extensibilityFeatures.nodes}
              plans={plans.nodes}
            />
            <PricingFeatureTable
              title="Governance &amp; Compliance"
              features={complianceFeatures.nodes}
              plans={plans.nodes}
            />
            <PricingFeatureTable
              title="Infrastructure &amp; Security"
              features={infrastructureFeatures.nodes}
              plans={plans.nodes}
            />
            <PricingFeatureTable
              title="Support &amp; Services"
              features={servicesFeatures.nodes}
              plans={plans.nodes}
            />
          </div>
        </section>
      </StickyContainer>

      <section className="max-w-6xl mx-auto px-4 md:px-6 space-y-12">
        <div className="space-y-6">
          <h4 className="font-medium leading-tight text-xl">
            All plans include
          </h4>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
          {page.usps.map((usp) => (
            <USPCardRedesigned key={usp.id} {...usp} theme="light" />
          ))}
        </div>
      </section>

      {contentBlock ? (
        <section className="bg-whisper py-20">
          <div className="gap-10 grid md:grid-cols-2 items-center max-w-6xl mx-auto px-4 md:px-6">
            <div className="space-y-4">
              <p className="font-medium text-sm tracking-wide uppercase text-rose">
                {contentBlock.subtitle}
              </p>
              <h3 className="font-bold text-2xl">{contentBlock.title}</h3>
              <p className="text-light-haiti">{contentBlock.content}</p>
              <div>
                <Link
                  to={contentBlock.ctaTo}
                  className="font-medium group text-rose"
                >
                  {contentBlock.cta}
                  <span className="duration-100 group-hover:translate-x-1 inline-block ml-1 transform transition-transform">
                    &#8594;
                  </span>
                </Link>
              </div>
            </div>
            <div className="md:w-full mx-auto w-3/4">
              <Img
                image={contentBlock.image}
                title={contentBlock.title}
                alt={contentBlock.title}
                className="rounded"
                maxWidth={760}
                transforms={['quality=value:75']}
                withWebp
              />
            </div>
          </div>
        </section>
      ) : null}
      <section className="max-w-4xl mx-auto px-4 md:px-6 space-y-12">
        <div className="mx-auto space-y-6 text-center w-full md:w-3/4">
          <h2 className="font-bold leading-tight text-3xl md:text-5xl">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="divide-y space-y-5">
          {pricingFaqCategory.faqs.map((faq, index) => {
            const isOpen = index === 0;

            return (
              <div key={faq.id} className="w-full">
                <FAQCard {...faq} open={isOpen} />
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export const query = graphql`
  query pricingPageQuery {
    cards: allGraphCmsPricingCard(sort: { fields: createdAt, order: ASC }) {
      nodes {
        id
        bullets
        ctaLink
        ctaText
        description
        name
        plans {
          id
          formattedAnnualPrice
          formattedMonthlyPrice
          headline
        }
        theme
      }
    }
    plans: allGraphCmsPricingPlan(sort: { fields: createdAt, order: ASC }) {
      nodes {
        id
        annualPrice
        description
        formattedAnnualPrice
        formattedMonthlyPrice
        monthlyPrice
        name
        entries {
          __typename
          ... on GraphCMS_PlanEntryBoolean {
            id
            included
            planFeature {
              id
            }
          }
          ... on GraphCMS_PlanEntryNumeric {
            id
            value
            planFeature {
              id
            }
          }
          ... on GraphCMS_PlanEntryText {
            id
            text: value
            planFeature {
              id
            }
          }
        }
      }
    }
    limitFeatures: allGraphCmsPlanFeature(
      filter: { category: { eq: LIMITS } }
    ) {
      nodes {
        id
        category
        comingSoon
        name
        description
      }
    }
    developmentFeatures: allGraphCmsPlanFeature(
      filter: { category: { eq: DEVELOPMENT } }
    ) {
      nodes {
        id
        category
        comingSoon
        name
        description
      }
    }
    contentFeatures: allGraphCmsPlanFeature(
      filter: { category: { eq: CONTENT } }
    ) {
      nodes {
        id
        category
        comingSoon
        name
        description
      }
    }
    extensibilityFeatures: allGraphCmsPlanFeature(
      filter: { category: { eq: EXTENSIBILITY } }
    ) {
      nodes {
        id
        category
        comingSoon
        name
        description
      }
    }
    complianceFeatures: allGraphCmsPlanFeature(
      filter: { category: { eq: COMPLIANCE } }
    ) {
      nodes {
        id
        category
        comingSoon
        name
        description
      }
    }
    infrastructureFeatures: allGraphCmsPlanFeature(
      filter: { category: { eq: INFRASTRUCTURE } }
    ) {
      nodes {
        id
        category
        comingSoon
        name
        description
      }
    }
    servicesFeatures: allGraphCmsPlanFeature(
      filter: { category: { eq: SERVICES } }
    ) {
      nodes {
        id
        category
        comingSoon
        name
        description
      }
    }
    pricingFaqCategory: graphCmsFaqCategory(
      slug: { eq: "payment-plans-and-pricing" }
    ) {
      faqs {
        answer
        id
        question
      }
    }
  }
`;

export default PricingBaseTemplate;
